import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const fetchTimesheetsSheet = createAsyncThunk(
  'timesheetsDashboard/fetchSheet',
  async ({ uid, startDate, endDate }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/timesheets/getSheet`, {
        signal,
        headers: { 'Content-Type': 'application/json' },
        params: { UID: uid, startDate, endDate },
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('timesheetsDashboard/fetchSheet failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch timesheets');
    }
  }
);

export const saveTimesheetComment = createAsyncThunk(
  'timesheetsDashboard/saveComment',
  async ({ commentData, userEmail }, { signal, rejectWithValue }) => {
    try {
      const payload = { ...(commentData || {}), UserEmail: userEmail };
      await axios.post(`${API_URL}/api/timesheets/saveComment`, payload, {
        signal,
        headers: { 'Content-Type': 'application/json' },
      });
      return { success: true };
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('timesheetsDashboard/saveComment failed', e);
      return rejectWithValue(e?.message || 'Failed to save comment');
    }
  }
);

export const exportTimesheetsDashboardCsv = createAsyncThunk(
  'timesheetsDashboard/exportCsv',
  async (_, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/export/timesheets`, {
        signal,
        responseType: 'blob',
      });

      const file = new Blob([res.data], { type: 'text/csv' });
      const fileURL = URL.createObjectURL(file);
      const tempLink = document.createElement('a');
      tempLink.href = fileURL;
      tempLink.setAttribute('download', 'Timesheet Dashboard.csv');
      tempLink.style.display = 'none';
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      URL.revokeObjectURL(fileURL);

      return { success: true };
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('timesheetsDashboard/exportCsv failed', e);
      return rejectWithValue(e?.message || 'Failed to export timesheets');
    }
  }
);

export const addTimesheetCategory = createAsyncThunk(
  'timesheetsDashboard/addCategory',
  async ({ userEmail, categoryName, dateAdded }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/timesheets/addCategory`,
        { UserEmail: userEmail, CategoryName: categoryName, DateAdded: dateAdded },
        { signal, headers: { 'Content-Type': 'application/json' } }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('timesheetsDashboard/addCategory failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to add category');
    }
  }
);

export const saveTimesheetData = createAsyncThunk(
  'timesheetsDashboard/saveTimesheet',
  async ({ userEmail, data, posted }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/timesheets/saveTimesheet`,
        { UserEmail: userEmail, Data: data, Posted: posted },
        { signal, headers: { 'Content-Type': 'application/json' } }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('timesheetsDashboard/saveTimesheet failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to save timesheet');
    }
  }
);

export const fetchTimesheetAddSearchOptions = createAsyncThunk(
  'timesheetsDashboard/fetchAddSearchOptions',
  async (_, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/timesheets/addSearchOptions`, {
        signal,
        headers: { 'Content-Type': 'application/json' },
      });
      return res?.data?.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('timesheetsDashboard/fetchAddSearchOptions failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch search options');
    }
  }
);

const slice = createSlice({
  name: 'timesheetsDashboard',
  initialState: { error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('timesheetsDashboard/') && action.type.endsWith('/rejected'),
      (state, action) => {
        if (action?.meta?.aborted) return;
        state.error = action.payload || action.error?.message || null;
      }
    );
  },
});

export default slice.reducer;


